import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { CodeBlock } from './CodeBlock'
import { cn } from '@/lib/utils'

interface MarkdownRendererProps {
  content: string
  className?: string
}

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  return (
    <div className={cn('prose prose-sm max-w-none', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Code blocks - handle <pre><code> structure
          // This renders the full CodeBlock for fenced code blocks
          pre({ children, node }: any) {
            // Extract code element info from AST node
            const codeElement = node?.children?.[0]
            if (codeElement?.tagName === 'code') {
              const className = codeElement.properties?.className?.[0] || ''
              const codeContent = codeElement.children?.[0]?.value || ''
              return (
                <CodeBlock inline={false} className={className}>
                  {codeContent}
                </CodeBlock>
              )
            }
            // Fallback - shouldn't happen normally
            return <div className="my-4">{children}</div>
          },

          // Inline code only - <code> outside of <pre>
          // When code is inside pre, pre handler takes over
          code({ children }: any) {
            // Always render as inline code here
            // Block code is handled by pre handler above
            return (
              <code className="bg-gray-100 text-red-600 px-1.5 py-0.5 rounded text-sm font-mono">
                {children}
              </code>
            )
          },

        // Links
        a({ href, children, ...props }) {
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 hover:underline"
              {...props}
            >
              {children}
            </a>
          )
        },

        // Paragraphs
        p({ children, ...props }) {
          return (
            <p className="mb-3 last:mb-0 whitespace-pre-wrap" {...props}>
              {children}
            </p>
          )
        },

        // Headings
        h1({ children, ...props }) {
          return (
            <h1 className="text-2xl font-bold mb-3 mt-6 first:mt-0" {...props}>
              {children}
            </h1>
          )
        },
        h2({ children, ...props }) {
          return (
            <h2 className="text-xl font-bold mb-3 mt-5 first:mt-0" {...props}>
              {children}
            </h2>
          )
        },
        h3({ children, ...props }) {
          return (
            <h3 className="text-lg font-bold mb-2 mt-4 first:mt-0" {...props}>
              {children}
            </h3>
          )
        },
        h4({ children, ...props }) {
          return (
            <h4 className="text-base font-bold mb-2 mt-3 first:mt-0" {...props}>
              {children}
            </h4>
          )
        },
        h5({ children, ...props }) {
          return (
            <h5 className="text-sm font-bold mb-2 mt-3 first:mt-0" {...props}>
              {children}
            </h5>
          )
        },
        h6({ children, ...props }) {
          return (
            <h6 className="text-xs font-bold mb-2 mt-3 first:mt-0" {...props}>
              {children}
            </h6>
          )
        },

        // Lists
        ul({ children, ...props }) {
          return (
            <ul className="list-disc list-outside ml-4 mb-3 space-y-1" {...props}>
              {children}
            </ul>
          )
        },
        ol({ children, ...props }) {
          return (
            <ol className="list-decimal list-outside ml-4 mb-3 space-y-1" {...props}>
              {children}
            </ol>
          )
        },
        li({ children, ...props }) {
          return (
            <li className="ml-1" {...props}>
              {children}
            </li>
          )
        },

        // Blockquote
        blockquote({ children, ...props }) {
          return (
            <blockquote
              className="border-l-4 border-gray-300 pl-4 py-1 my-3 bg-gray-50 italic"
              {...props}
            >
              {children}
            </blockquote>
          )
        },

        // Tables
        table({ children, ...props }) {
          return (
            <div className="my-4 overflow-x-auto">
              <table className="min-w-full border-collapse border border-gray-300" {...props}>
                {children}
              </table>
            </div>
          )
        },
        thead({ children, ...props }) {
          return (
            <thead className="bg-gray-100" {...props}>
              {children}
            </thead>
          )
        },
        tbody({ children, ...props }) {
          return <tbody {...props}>{children}</tbody>
        },
        tr({ children, ...props }) {
          return (
            <tr className="border-b border-gray-300" {...props}>
              {children}
            </tr>
          )
        },
        th({ children, ...props }) {
          return (
            <th className="border border-gray-300 px-3 py-2 text-left font-semibold" {...props}>
              {children}
            </th>
          )
        },
        td({ children, ...props }) {
          return (
            <td className="border border-gray-300 px-3 py-2" {...props}>
              {children}
            </td>
          )
        },

        // Horizontal rule
        hr({ ...props }) {
          return <hr className="my-4 border-gray-300" {...props} />
        },

        // Strong and emphasis
        strong({ children, ...props }) {
          return (
            <strong className="font-bold" {...props}>
              {children}
            </strong>
          )
        },
        em({ children, ...props }) {
          return (
            <em className="italic" {...props}>
              {children}
            </em>
          )
        },

        // Strikethrough (from GFM)
        del({ children, ...props }) {
          return (
            <del className="line-through text-gray-500" {...props}>
              {children}
            </del>
          )
        },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
